// src/services/websocket.js
import { ref, reactive } from "vue";
import { useUserStore } from "@/stores/userStore";
import Config from "../config.js";

class WebSocketService {
    constructor() {
        this.ws = null;
        this.isConnected = ref(false);
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000;
        this.listeners = reactive(new Map()); // code → handler
        this.globalListeners = [];
    }

    // Подписка на определённый код события
    on(code, callback) {
        if (!this.listeners.has(code)) {
            this.listeners.set(code, []);
        }
        this.listeners.get(code).push(callback);
    }

    onAll(callback) {
        this.globalListeners.push(callback);
    }    

    // Отписка (опционально)
    off(code, callback) {
        if (this.listeners.has(code)) {
            const callbacks = this.listeners.get(code);
            const index = callbacks.indexOf(callback);
            if (index > -1) callbacks.splice(index, 1);
            if (callbacks.length === 0) this.listeners.delete(code);
        }
    }

    offAll(callback) {
        const index = this.globalListeners.indexOf(callback);
        if (index > -1) {
            this.globalListeners.splice(index, 1);
        }
    }

    connect() {
        if (this.ws && this.isConnected.value) return;

        const userStore = useUserStore();
        if (!userStore.token) {
            console.warn("[WS] No auth token, cannot connect");
            return;
        }

        try {
            const url = `${Config.WEBSOCKET}?token=${encodeURIComponent(
                userStore.token
            )}`;
            this.ws = new WebSocket(url);

            this.ws.onopen = () => {
                console.log("[WS] Connected");
                this.isConnected.value = true;
                this.reconnectAttempts = 0;
            };

            this.ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    this.handleMessage(message);
                } catch (e) {
                    console.error("[WS] Invalid message", event.data, e);
                }
            };

            this.ws.onclose = () => {
                console.log("[WS] Disconnected");
                this.isConnected.value = false;
                this.ws = null;
                this.attemptReconnect();
            };

            this.ws.onerror = (error) => {
                console.error("[WS] Error", error);
            };
        } catch (err) {
            console.error("[WS] Connection failed", err);
            this.attemptReconnect();
        }
    }

    disconnect() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
            this.isConnected.value = false;
        }
    }

    sendMessage(data) {
        if (this.ws && this.isConnected.value) {
            this.ws.send(JSON.stringify(data));
        } else {
            console.warn("[WS] Not connected, cannot send message");
        }
    }

    handleMessage(message) {
        const { code } = message;
        const specificHandlers = this.listeners.get(code) || [];

        if (specificHandlers.length > 0) {
            [...specificHandlers].forEach((handler) => {
                try {
                    handler(message);
                } catch (e) {
                    console.error(`[WS] Handler error for code ${code}`, e);
                }
            });
        } else {
            if (this.globalListeners.length === 0) {
                console.warn(`[WS] No handlers for code ${code}`, message);
            }
        }

        [...this.globalListeners].forEach((handler) => {
            try {
                handler(message);
            } catch (e) {
                console.error(`[WS] Global handler error`, e);
            }
        });
    }

    attemptReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(
                `[WS] Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`
            );
            setTimeout(() => this.connect(), this.reconnectDelay);
        } else {
            console.error("[WS] Max reconnection attempts reached");
        }
    }
}

export const wsService = new WebSocketService();
